import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { shouldPromptForPermission, requestPermission, currentPermission } from '@/lib/notifications';
import { toast } from 'sonner';

const ReminderPrompt = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) { setVisible(false); return; }
    // Delay so it doesn't feel intrusive on first load
    const t = setTimeout(() => setVisible(shouldPromptForPermission()), 1500);
    return () => clearTimeout(t);
  }, [user]);

  const enable = async () => {
    const result = await requestPermission();
    setVisible(false);
    if (result === 'granted') {
      toast.success('Reminders on', { description: "We'll nudge you ~22 hours after your last visit so your streak stays alive." });
    } else if (result === 'denied') {
      toast('Reminders off', { description: 'You can re-enable them in your browser site settings anytime.' });
    }
  };

  const dismiss = () => {
    setVisible(false);
    // Remember dismissal so we don't nag on every dashboard visit
    localStorage.setItem('wellth_notif_permission_asked', new Date().toISOString());
  };

  if (!visible || currentPermission() === 'unsupported') return null;

  return (
    <div
      className="mb-8 flex flex-wrap items-center gap-4 justify-between bg-white border border-[#E5E1D8] rounded-2xl p-5 sm:p-6"
      data-testid="reminder-prompt"
    >
      <div className="flex items-start gap-4 flex-1 min-w-[240px]">
        <div className="w-10 h-10 rounded-full bg-[#F2EFE9] flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-[#4A7C59]" strokeWidth={1.5} />
        </div>
        <div>
          <p className="font-medium text-[#1A2421]">Keep your streak alive.</p>
          <p className="text-sm text-[#4A5A53] mt-1 font-light leading-relaxed">
            Turn on browser reminders and we&rsquo;ll ping you ~22 hours after your last visit &mdash; a gentle 2-hour warning before your streak resets.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={enable}
          className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-5"
          data-testid="reminder-enable-button"
        >
          Turn on reminders
        </Button>
        <Button
          onClick={dismiss}
          variant="ghost"
          size="icon"
          className="rounded-full text-[#4A5A53]"
          data-testid="reminder-dismiss-button"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
};

export default ReminderPrompt;
